import { useState, type FormEvent } from 'react';
import { useCanWrite } from '@core/auth/access';
import { Modal, TabPanels } from '@core/ui/Modal';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { ObjectHistory } from '../../history/ObjectHistory';
import { Button } from '@core/ui/Button';
import { DeleteConfirm, FormError, ModalFooter, fieldInput as input, fieldLabel as label, readOnlyFields, submitOnCtrlEnter } from '@core/ui/form';
import { data, useTable } from '@core/store';
import { planProjectSave, type ProjectDraft } from './planner';
import { PROJECT_PALETTE } from '@core/palette';
import { TogglePill } from '@core/ui/TogglePill';

const FORM_ID = 'project-form'; // Save живёт в футере окна, вне формы


// Проект и его подпроекты одной карточкой, как в старом. Удаление проекта уносит его задачи
// (каскад в базе, всё в журнале); удаление подпроекта переводит задачи в строку проекта.
export function ProjectModal({ draft, onClose }: { draft: ProjectDraft; onClose: () => void }) {
  const canWrite = useCanWrite();
  const projects = useTable('project');
  const subprojects = useTable('subproject');
  const tasks = useTable('task');
  const lanes = useTable('timeline_lane');
  const laneLinks = useTable('lane_project');
  const laneList = [...lanes.values()].sort((a, b) => a.position - b.position);
  const [d, setD] = useState(draft);
  const [tab, setTab] = useState<'main' | 'history'>('main');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taskCount = d.id ? [...tasks.values()].filter((t) => t.project_id === d.id).length : 0;

  const setSub = (i: number, name: string) => setD({ ...d, subprojects: d.subprojects.map((s, j) => (j === i ? { ...s, name } : s)) });
  const removeSub = (i: number) => setD({ ...d, subprojects: d.subprojects.filter((_, j) => j !== i) });

  async function save(e: FormEvent) {
    e.preventDefault();
    const plan = planProjectSave(d, projects.values(), subprojects.values(), () => crypto.randomUUID(), laneLinks.values());
    if ('error' in plan) { setError(plan.error); return; }
    setBusy(true); setError(null);
    const r = plan.changes.length ? await data.applyChanges(plan.changes, { kind: d.id ? 'edit_project' : 'create_project', project_id: plan.projectId }) : { ok: true as const };
    setBusy(false);
    if (!r.ok) { setError('error' in r ? r.error : 'Failed'); return; }
    onClose();
  }

  async function remove() {
    if (!d.id) return;
    setBusy(true);
    const r = await data.applyChanges([{ table: 'project', id: d.id, delete: true }], { kind: 'delete_project', project_id: d.id });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  const footer = (
    <ModalFooter form={FORM_ID} busy={busy} onCancel={onClose} readOnly={!canWrite} left={d.id && <DeleteConfirm label="Delete project" question={<>Delete «{d.name}» with its {taskCount} task(s)? They go to the history and can be restored later.</>} busy={busy} onDelete={() => void remove()} />} />
  );
  const form = (
    <form id={FORM_ID} onSubmit={(e) => void save(e)} onKeyDown={canWrite ? submitOnCtrlEnter : undefined} className="flex flex-col gap-4">
      <label className={label}>Name
        <input className={input} value={d.name} autoFocus required onChange={(e) => setD({ ...d, name: e.target.value })} />
      </label>
      {/* цвет проекта — пилюли и карточки Release Plan; палитра старого плана плюс любой свой */}
      <div className={label}>Colour
        <div className="flex items-center gap-2 flex-wrap" role="radiogroup" aria-label="Project colour">
          {PROJECT_PALETTE.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={d.color === c} aria-label={c} title={c}
              className={`min-h-0 w-8 h-8 rounded-full border-2 ${d.color === c ? 'border-gray-900 scale-110' : 'border-white'} shadow`} style={{ background: c }}
              onClick={() => setD({ ...d, color: c })} />
          ))}
          <input type="color" className="min-h-0 w-8 h-8 p-0 border border-gray-300 rounded cursor-pointer" value={d.color} title="Any other colour" aria-label="Custom colour" onChange={(e) => setD({ ...d, color: e.target.value })} />
        </div>
      </div>
      {/* ленты Timeline: без ленты проект на Timeline не показывается */}
      {laneList.length > 0 && (
        <div className={label}>Timeline lanes
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Timeline lanes">
            {laneList.map((l) => {
              const on = d.laneIds.includes(l.id);
              return <TogglePill key={l.id} on={on} label={l.name} data={{ 'data-project-lane': l.id }} onClick={() => setD({ ...d, laneIds: on ? d.laneIds.filter((x) => x !== l.id) : [...d.laneIds, l.id] })} />;
            })}
          </div>
          {d.laneIds.length === 0 && <span className="text-xs text-gray-500 font-normal">Not on the Timeline until it is in a lane.</span>}
        </div>
      )}
      <fieldset className="border border-line rounded-md p-3">
        <legend className="px-1 font-medium">Subprojects</legend>
        <div className="flex flex-col gap-2">
          {d.subprojects.map((s, i) => (
            <div key={s.id ?? `new-${i}`} className="flex items-center gap-2">
              <input className={input} value={s.name} placeholder="Subproject name" onChange={(e) => setSub(i, e.target.value)} aria-label={`Subproject ${i + 1}`} />
              <Button variant="ghost" onClick={() => removeSub(i)} aria-label="Remove subproject" title={s.id ? 'Remove — its tasks move to the project row' : 'Remove'}>×</Button>
            </div>
          ))}
          <Button variant="secondary" className="self-start" onClick={() => setD({ ...d, subprojects: [...d.subprojects, { id: null, name: '' }] })}>+ Subproject</Button>
        </div>
      </fieldset>
      <FormError error={error} />
    </form>
  );
  const head = d.id ? <SegmentedControl role="tabs" label="Project card" className="mb-4" value={tab} onChange={setTab} items={[{ key: 'main', label: 'Project' }, { key: 'history', label: 'History' }]} /> : undefined;
  return (
    <Modal open onClose={onClose} title={d.id ? 'Project' : 'New project'} head={head} footer={footer}>
      {d.id ? <TabPanels active={tab} panels={[['main', readOnlyFields(!canWrite, form)], ['history', <ObjectHistory table="project" id={d.id} active={tab === 'history'} />]]} /> : readOnlyFields(!canWrite, form)}
    </Modal>
  );
}
